import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Button,
  Typography,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Avatar,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Build as ToolIcon,
  Timeline as PhaseIcon,
  Info as InfoIcon,
  Description as DescriptionIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import jwtDecode from 'jwt-decode';
import { BASEURL } from '../../Constants';
import Nav from './nav';

const Integrations = () => {
  const [integrations, setIntegrations] = useState([]);
  const [filteredIntegrations, setFilteredIntegrations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [formValues, setFormValues] = useState({
    name: '',
    tool: '',
    phase: '',
    description: '',
    additional_info: '',
  });
  const navigate = useNavigate();
  const { pentest_id } = useParams();

  // Function to get token from sessionStorage
  const getToken = () => {
    const token = sessionStorage.getItem('token');
    if (!token) {
      console.error('No token found');
      navigate('/');
    }
    return token;
  };

  // Fetch integrations from API
  useEffect(() => {
    const token = getToken();
    fetch(`${BASEURL}/pentest/integrations?pentest_id=${pentest_id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          if (data.message === 'Invalid token') {
            navigate('/');
          } else {
            console.error('Error fetching integrations:', data.message);
          }
        } else {
          setIntegrations(data.integrations);
          setFilteredIntegrations(data.integrations);
        }
      })
      .catch((error) => {
        console.error('Error fetching integrations:', error);
      });
  }, []);

  // Handle search query change
  const handleSearchChange = (event) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);
    const filtered = integrations.filter((integration) =>
      integration.name.toLowerCase().includes(query) ||
      integration.tool.toLowerCase().includes(query) ||
      integration.phase.toLowerCase().includes(query) ||
      integration.description.toLowerCase().includes(query) ||
      (integration.additional_info && integration.additional_info.toLowerCase().includes(query))
    );
    setFilteredIntegrations(filtered);
  };

  // Handle create new integration
  const handleCreateClick = () => {
    setFormValues({
      name: '',
      tool: '',
      phase: '',
      description: '',
      additional_info: '',
    });
    setIsCreateDialogOpen(true);
  };

  const handleCreateDialogClose = () => {
    setIsCreateDialogOpen(false);
  };

  const handleCreateSubmit = () => {
    const token = getToken();
    // Create the new integration object
    const newIntegration = { ...formValues };
    // Update the local integrations array
    const updatedIntegrations = [...integrations, newIntegration];
    setIntegrations(updatedIntegrations);
    setFilteredIntegrations(updatedIntegrations);
    // Send PUT request with the updated integrations array
    fetch(`${BASEURL}/pentest/integrations?pentest_id=${pentest_id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
      body: JSON.stringify(updatedIntegrations),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          console.error('Error creating integration:', data.message);
        } else {
          setIsCreateDialogOpen(false);
        }
      })
      .catch((error) => {
        console.error('Error creating integration:', error);
      });
  };

  // Handle edit integration
  const handleEditClick = (integration) => {
    setSelectedIntegration(integration);
    setFormValues({
      name: integration.name,
      tool: integration.tool,
      phase: integration.phase,
      description: integration.description,
      additional_info: integration.additional_info,
    });
    setIsEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setIsEditDialogOpen(false);
    setSelectedIntegration(null);
  };

  const handleEditSubmit = () => {
    const token = getToken();
    // Update the integration in the local integrations array
    const updatedIntegrations = integrations.map((integration) =>
      integration.name === selectedIntegration.name ? { ...formValues } : integration
    );
    setIntegrations(updatedIntegrations);
    // Update the filtered integrations based on the current search query
    const filtered = updatedIntegrations.filter((integration) =>
      integration.name.toLowerCase().includes(searchQuery)
    );
    setFilteredIntegrations(filtered);
    // Send PUT request with the updated integrations array
    fetch(`${BASEURL}/pentest/integrations?pentest_id=${pentest_id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
      body: JSON.stringify(updatedIntegrations),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          console.error('Error updating integration:', data.message);
        } else {
          setIsEditDialogOpen(false);
          setSelectedIntegration(null);
        }
      })
      .catch((error) => {
        console.error('Error updating integration:', error);
      });
  };

  // Handle delete integration
  const handleDeleteClick = (integration) => {
    setSelectedIntegration(integration);
    setIsDeleteConfirmOpen(true);
  };

  const handleDeleteConfirmClose = () => {
    setIsDeleteConfirmOpen(false);
    setSelectedIntegration(null);
  };

  const handleDeleteConfirm = () => {
    const token = getToken();
    // Remove the integration from the local integrations array
    const updatedIntegrations = integrations.filter(
      (integration) => integration.name !== selectedIntegration.name
    );
    setIntegrations(updatedIntegrations);
    // Update the filtered integrations based on the current search query
    const filtered = updatedIntegrations.filter((integration) =>
      integration.name.toLowerCase().includes(searchQuery)
    );
    setFilteredIntegrations(filtered);
    // Send PUT request with the updated integrations array
    fetch(`${BASEURL}/pentest/integrations?pentest_id=${pentest_id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
      body: JSON.stringify(updatedIntegrations),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          console.error('Error deleting integration:', data.message);
        } else {
          setIsDeleteConfirmOpen(false);
          setSelectedIntegration(null);
        }
      })
      .catch((error) => {
        console.error('Error deleting integration:', error);
      });
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Nav />
      {/* Search Field */}
      <Card sx={{ mb: 2,  alignItems: 'center' }}>
        <Typography variant="h6" gutterBottom sx={{ textAlign:"left",fontWeight: 'bold', marginLeft: '20px',marginTop: '20px' }}>
          Integrations
        </Typography>
        <Typography variant="body2" gutterBottom sx={{ textAlign:"left",  marginLeft: '20px' }}>
            Below is a list of tools the copilot has access too.
        </Typography>
        <TextField
          variant="outlined"
          placeholder="Search tools"
          size="small"
          sx={{padding:"10px",width:"50%"}}
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1 }} />,
          }}
        />
      </Card>
      <Grid container spacing={2}>
        {/* First card: Create new integration */}
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardHeader
              avatar={
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <AddIcon />
                </Avatar>
              }
              title="Create New Integration"
            />
            <CardContent sx={{ flexGrow: 1 }}>
              <Typography variant="body2">
                Click the button below to create a new integration.
              </Typography>
            </CardContent>
            <CardActions>
              <Button
                startIcon={<AddIcon />}
                onClick={handleCreateClick}
                variant="contained"
                fullWidth
              >
                Create
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Display integrations */}
        {filteredIntegrations.map((integration) => (
          <Grid item xs={12} sm={6} md={4} key={integration.name}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardHeader
                avatar={
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    {integration.name.charAt(0).toUpperCase()}
                  </Avatar>
                }
                action={
                  <IconButton
                    onClick={() => handleDeleteClick(integration)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                }
                title={integration.name}
                subheader={integration.tool}
              />
              <CardContent sx={{ flexGrow: 1 }}>
                <Box display="flex" alignItems="center" mb={1}>
                  <PhaseIcon sx={{ mr: 1 }} color="action" />
                  <Typography variant="body2" color="textSecondary">
                    <strong>Phase:</strong> {integration.phase}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" mb={1}>
                  <DescriptionIcon sx={{ mr: 1 }} color="action" />
                  <Typography variant="body2" color="textSecondary">
                    {integration.description}
                  </Typography>
                </Box>
                {integration.additional_info && (
                  <Box display="flex" alignItems="center">
                    <InfoIcon sx={{ mr: 1 }} color="action" />
                    <Typography variant="body2" color="textSecondary">
                      {integration.additional_info}
                    </Typography>
                  </Box>
                )}
              </CardContent>
              <CardActions>
                <Button
                  startIcon={<EditIcon />}
                  onClick={() => handleEditClick(integration)}
                  variant="outlined"
                  fullWidth
                >
                  Edit
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onClose={handleEditDialogClose} fullWidth>
        <DialogTitle>Edit Integration</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Name"
            type="text"
            fullWidth
            value={formValues.name}
            onChange={(e) =>
              setFormValues({ ...formValues, name: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label="Tool"
            type="text"
            fullWidth
            value={formValues.tool}
            onChange={(e) =>
              setFormValues({ ...formValues, tool: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label="Phase"
            type="text"
            fullWidth
            value={formValues.phase}
            onChange={(e) =>
              setFormValues({ ...formValues, phase: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label="Description"
            type="text"
            fullWidth
            multiline
            value={formValues.description}
            onChange={(e) =>
              setFormValues({ ...formValues, description: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label="Additional Info"
            type="text"
            fullWidth
            multiline
            value={formValues.additional_info}
            onChange={(e) =>
              setFormValues({
                ...formValues,
                additional_info: e.target.value,
              })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditDialogClose}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteConfirmOpen}
        onClose={handleDeleteConfirmClose}
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the integration{' '}
            <strong>{selectedIntegration?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteConfirmClose}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onClose={handleCreateDialogClose} fullWidth>
        <DialogTitle>Create New Integration</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Name"
            type="text"
            fullWidth
            value={formValues.name}
            onChange={(e) =>
              setFormValues({ ...formValues, name: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label="Tool"
            type="text"
            fullWidth
            value={formValues.tool}
            onChange={(e) =>
              setFormValues({ ...formValues, tool: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label="Phase"
            type="text"
            fullWidth
            value={formValues.phase}
            onChange={(e) =>
              setFormValues({ ...formValues, phase: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label="Description"
            type="text"
            fullWidth
            multiline
            value={formValues.description}
            onChange={(e) =>
              setFormValues({ ...formValues, description: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label="Additional Info"
            type="text"
            fullWidth
            multiline
            value={formValues.additional_info}
            onChange={(e) =>
              setFormValues({
                ...formValues,
                additional_info: e.target.value,
              })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateDialogClose}>Cancel</Button>
          <Button onClick={handleCreateSubmit} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Integrations;
